import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import {
    cancelDebitNote,
    deleteDebitNote,
    generateEDebitNote,
    getDebitNoteDetails,
    getDebitNoteEinvoiceConfig,
} from '../../services/DebitNoteServices';
import { fetchAndShowDebitNotePdf } from '../../utils/debitNotePdf';
import InvoiceEinvoiceLockBanner from '../../components/InvoiceEinvoiceLockBanner';
import InvoiceEinvoiceSection from '../../components/InvoiceEinvoiceSection';
import {
    buildDebitNoteTotals,
    getDebitNoteTypeLabel,
} from './DebitNoteUtils';
import TableRowsDebitNote from './TableRowsDebitNote';
import moment from 'moment';

function DebitNoteEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({});
    const [productLines, setProductLines] = useState([]);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [edebitConfig, setEdebitConfig] = useState(null);
    const [edebitResult, setEdebitResult] = useState(null);
    const [isFetchingIrn, setIsFetchingIrn] = useState(false);
    const isCancelled = inputs.status === false || inputs.status === 0;

    const totals = useMemo(
        () => buildDebitNoteTotals(productLines, invoiceLines),
        [productLines, invoiceLines],
    );

    useEffect(() => {
        setInProgress(true);
        getDebitNoteDetails(cookies, id)
            .then((response) => {
                setInputs({
                    ...response,
                    dn_date: moment(response.dn_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
                    dn_no: response.dn_no,
                    status: response.status,
                });
                setProductLines(response.product_lines || []);
                setInvoiceLines(response.invoice_lines || []);
                if (response.einvoice_irn) {
                    setEdebitResult({
                        einvoice_irn: response.einvoice_irn,
                        einvoice_ack: response.einvoice_ack,
                    });
                }
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load Debit Note.' }, 'Load Failed');
            })
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.dn_date || !inputs.invoice_id) {
            setEdebitConfig(null);
            return;
        }
        getDebitNoteEinvoiceConfig(
            cookies,
            inputs.plant_id,
            moment(inputs.dn_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
            totals.debit_amt || inputs.debit_amt || 0,
            inputs.invoice_id,
        ).then((response) => setEdebitConfig({
            ...response,
            einvoice_irn: edebitResult?.einvoice_irn || inputs.einvoice_irn,
            einvoice_ack: edebitResult?.einvoice_ack || inputs.einvoice_ack,
        })).catch(() => setEdebitConfig(null));
    }, [cookies, inputs.plant_id, inputs.dn_date, inputs.invoice_id, inputs.debit_amt, inputs.einvoice_irn, inputs.einvoice_ack, totals.debit_amt, edebitResult]);

    const onPrintDebitNote = () => {
        setInProgress(true);
        fetchAndShowDebitNotePdf(cookies, id, { title: 'Debit Note PDF' })
            .finally(() => setInProgress(false));
    };

    const onGenerateEDebitNote = () => {
        setIsFetchingIrn(true);
        generateEDebitNote(cookies, id)
            .then((response) => {
                setEdebitResult(response);
                setInputs((values) => ({
                    ...values,
                    einvoice_irn: response.einvoice_irn || '',
                    einvoice_ack: response.einvoice_ack || '',
                    einvoice_status: response.einvoice_status,
                    einvoice_status_label: 'Generated',
                    debit_note_lock_mode: 'locked',
                    is_debit_note_editable: false,
                }));
                Swal.fire(response.message || `E-Debit Note IRN saved: ${response.einvoice_irn}`, '', 'success');
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: error?.message }, 'Generate IRN Failed');
            })
            .finally(() => setIsFetchingIrn(false));
    };

    const onCancelDebitNote = () => {
        const hasIrn = Boolean(inputs.einvoice_irn || edebitResult?.einvoice_irn);
        const cancelHtml = hasIrn
            ? `<p>This Debit Note has an E-Debit Note IRN. Per GST rules, the IRN will be <b>cancelled on the GST portal</b> (within 24 hours of IRN generation), then this Debit Note is marked <b>inactive</b>.</p>
               <label for="swal-cancel-reason" style="display:block;margin-top:12px;font-weight:600;">GST cancel reason</label>
               <select id="swal-cancel-reason" class="form-control" style="margin-bottom:8px;">
                 <option value="2">Data entry mistake</option>
                 <option value="1">Duplicate</option>
                 <option value="3">Order cancelled</option>
                 <option value="4">Others</option>
               </select>
               <input id="swal-cancel-remarks" class="form-control" placeholder="Remarks (optional, max 100 chars)" maxlength="100" />`
            : '<p>Mark this Debit Note as <b>cancelled (inactive)</b>? Values are retained for audit.</p>';

        Swal.fire({
            title: hasIrn ? 'Cancel E-Debit Note IRN?' : 'Cancel this Debit Note?',
            html: cancelHtml,
            icon: hasIrn ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: hasIrn ? 'Cancel IRN & Debit Note' : 'Yes, cancel',
            cancelButtonText: 'No',
            focusCancel: true,
            preConfirm: () => {
                if (!hasIrn) {
                    return {
                        cancel_edebit_note: false,
                        cancel_reason: '2',
                        cancel_remarks: 'Debit Note cancelled',
                    };
                }
                return {
                    cancel_edebit_note: true,
                    cancel_reason: document.getElementById('swal-cancel-reason')?.value || '2',
                    cancel_remarks: document.getElementById('swal-cancel-remarks')?.value || 'Debit Note cancelled',
                };
            },
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelDebitNote(cookies, id, result.value || {})
                .then((response) => {
                    Swal.fire(response.message || 'Cancelled!', '', 'success');
                    navigate('/DebitNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Cancel Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    const onDeleteDebitNote = () => {
        Swal.fire({
            title: 'Delete this cancelled Debit Note?',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deleteDebitNote(cookies, id)
                .then(() => {
                    Swal.fire('Deleted!', '', 'success');
                    navigate('/DebitNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Delete Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay inProgress={inProgress} />
            <div id="page-wrap">
                <form>
                    <FloatingControls tableLink="/DebitNoteTable" formLink="/DebitNote" addNewLabel="Add New" enableCancel={false} />
                    <FormPageLayout
                        moduleLabel="ACCOUNTS"
                        title="Update Debit Note"
                        subtitle="Review Debit Note details for sales return, rate difference, discount, or other adjustments."
                        modeLabel={`${getDebitNoteTypeLabel(inputs.dn_type).toUpperCase()} UPDATE MODE`}
                        headerExtra={(
                            <InvoiceEinvoiceLockBanner
                                mode={isCancelled ? 'cancelled' : inputs.debit_note_lock_mode}
                                message={isCancelled
                                    ? 'This Debit Note is inactive. Values are retained for audit only.'
                                    : inputs.debit_note_lock_message}
                            />
                        )}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-5 text-right">Plant Location</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.plant_name || inputs.plant_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Customer</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.customer_name || inputs.customer_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Site</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.site_name || inputs.consignee_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Invoice</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.invoice_no || inputs.invoice_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Debit Note Type</label>
                                        <input className="form-control col-sm-6" readOnly value={getDebitNoteTypeLabel(inputs.dn_type)} /><br />
                                        <label className="form-group col-sm-5 text-right">Voucher Mode</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.voucher_mode || ''} />
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Debit Note No.</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.dn_no || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Debit Note Date</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.dn_date || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">E-Debit Note IRN</label>
                                        <input className="form-control col-sm-6" readOnly value={edebitResult?.einvoice_irn || inputs.einvoice_irn || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Ack No.</label>
                                        <input className="form-control col-sm-6" readOnly value={edebitResult?.einvoice_ack || inputs.einvoice_ack || ''} />
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>S.No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th>Qty</th>
                                            <th>Basic Rate</th>
                                            <th>Amount</th>
                                            <th>Tax %</th>
                                            <th>Tax Amount</th>
                                            <th>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <TableRowsDebitNote rowsData={productLines} taxes={[]} dnType={inputs.dn_type} readOnly />
                                </table>
                            </div>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Doc Type</th>
                                            <th>Plant Name</th>
                                            <th>Project/Site Name</th>
                                            <th>Invoice Number</th>
                                            <th>Invoice Date</th>
                                            <th>Invoice Amount</th>
                                            <th>Received Amount</th>
                                            <th>Balance Amount</th>
                                            <th>Debit Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceLines.map((line, index) => (
                                            <tr key={`cn-edit-inv-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{line.doc_type || 'Invoice'}</td>
                                                <td>{line.plant_name}</td>
                                                <td>{line.site_name}</td>
                                                <td>{line.invoice_no}</td>
                                                <td>{line.invoice_date}</td>
                                                <td>{line.invoice_amt}</td>
                                                <td>{line.received_amt}</td>
                                                <td>{line.balance_amt}</td>
                                                <td>{line.debit_amt}</td>
                                                <td>{line.remarks || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-2 text-right">Remarks</label>
                                <textarea rows="2" className="form-control col-sm-10" readOnly={isCancelled} value={inputs.remarks || ''} />
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-3 text-right">Total Debit Note Value</label>
                                <input className="form-control col-sm-3" readOnly value={totals.debit_amt || inputs.debit_amt || 0} />
                            </div>

                            <InvoiceEinvoiceSection
                                config={edebitConfig}
                                einvoiceResult={edebitResult}
                                onGenerateEinvoice={!isCancelled ? onGenerateEDebitNote : undefined}
                                isFetchingIrn={isFetchingIrn}
                            />
                        </div>

                        <div className="footer company-form-footer text-center">
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={onPrintDebitNote}><i className="fa fa-print" aria-hidden="true" /> Print</Button>
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/DebitNoteTable')}><i className="fa fa-eye" aria-hidden="true" /> View</Button>
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}><i className="fa fa-home" aria-hidden="true" /> Home</Button>
                            {!isCancelled && (
                                <Button type="button" className="btn btn-cancel" style={{ width: '120px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onCancelDebitNote}>Cancel DN</Button>
                            )}
                            {isCancelled && (
                                <Button type="button" className="btn btn-delete" style={{ width: '80px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onDeleteDebitNote}>Delete</Button>
                            )}
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default DebitNoteEdit;
